import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { REQUEST_META_DATA } from '@seech-sub/core-ng';
import { Observable } from 'rxjs';
import { OperationsApiRoutes } from '../constants/operations.constants';
import {
  AffiliateAdministrativeCommand,
  AffiliateAuditPage,
  AffiliateCommissionDetail,
  AffiliateCommissionPage,
  AffiliateCreatorDetail,
  AffiliateCreatorPage,
  AffiliateCreatorSummary,
  AffiliateDeal,
  AffiliateDealPage,
  AffiliateLinkAdministration,
  AffiliateLinkPage,
} from '../models';

@Injectable({ providedIn: 'root' })
export class AffiliateProgramClientService {
  private readonly http = inject(HttpClient);
  private readonly quietContext = new HttpContext().set(REQUEST_META_DATA.HIDE_PROGRESS, true);

  searchCreators(searchTerm?: string, statusKey?: string): Observable<AffiliateCreatorPage> {
    return this.http.get<AffiliateCreatorPage>(OperationsApiRoutes.affiliateCreators, {
      params: this.query({ searchTerm, statusKey, page: 1, pageSize: 50 }), context: this.quietContext,
    });
  }

  getCreator(id: string): Observable<AffiliateCreatorDetail> {
    return this.http.get<AffiliateCreatorDetail>(OperationsApiRoutes.affiliateCreatorById(id), { context: this.quietContext });
  }

  creatorAction(id: string, action: 'restrict' | 'suspend' | 'restore' | 'close', command: AffiliateAdministrativeCommand): Observable<AffiliateCreatorSummary> {
    return this.http.post<AffiliateCreatorSummary>(OperationsApiRoutes.affiliateCreatorAction(id, action), command);
  }

  searchDeals(searchTerm?: string, statusKey?: string): Observable<AffiliateDealPage> {
    return this.http.get<AffiliateDealPage>(OperationsApiRoutes.affiliateDeals, {
      params: this.query({ searchTerm, statusKey, page: 1, pageSize: 50 }), context: this.quietContext,
    });
  }

  getDeal(id: string): Observable<AffiliateDeal> {
    return this.http.get<AffiliateDeal>(OperationsApiRoutes.affiliateDealById(id), { context: this.quietContext });
  }

  dealRiskAction(id: string, action: 'risk-restrict' | 'risk-restore', command: AffiliateAdministrativeCommand): Observable<AffiliateDeal> {
    return this.http.post<AffiliateDeal>(OperationsApiRoutes.affiliateDealAction(id, action), command);
  }

  searchLinks(searchTerm?: string, statusKey?: string, creatorId?: string): Observable<AffiliateLinkPage> {
    return this.http.get<AffiliateLinkPage>(OperationsApiRoutes.affiliateLinks, {
      params: this.query({ searchTerm, statusKey, creatorId, page: 1, pageSize: 50 }), context: this.quietContext,
    });
  }

  disableLink(id: string, command: AffiliateAdministrativeCommand): Observable<AffiliateLinkAdministration> {
    return this.http.post<AffiliateLinkAdministration>(OperationsApiRoutes.affiliateLinkDisable(id), command);
  }

  searchCommissions(creatorId?: string, entryTypeKey?: string, statusKey?: string): Observable<AffiliateCommissionPage> {
    return this.http.get<AffiliateCommissionPage>(OperationsApiRoutes.affiliateCommissions, {
      params: this.query({ creatorId, entryTypeKey, statusKey, page: 1, pageSize: 50 }), context: this.quietContext,
    });
  }

  getCommission(id: string): Observable<AffiliateCommissionDetail> {
    return this.http.get<AffiliateCommissionDetail>(OperationsApiRoutes.affiliateCommissionById(id), { context: this.quietContext });
  }

  searchAudit(targetTypeKey?: string, targetId?: string, actionKey?: string, outcomeKey?: string): Observable<AffiliateAuditPage> {
    return this.http.get<AffiliateAuditPage>(OperationsApiRoutes.affiliateAudit, {
      params: this.query({ targetTypeKey, targetId, actionKey, outcomeKey, page: 1, pageSize: 50 }), context: this.quietContext,
    });
  }

  private query(values: Record<string, string | number | undefined>): HttpParams {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(values)) if (value !== undefined && value !== '') params = params.set(key, value);
    return params;
  }
}
